/**
 * Утилиты для построения deeplink'ов в мобильные банковские приложения
 * для P2P переводов с предзаполненной суммой и реквизитами получателя.
 *
 * Важно: банки не публикуют официальной открытой документации по deeplink'ам
 * для P2P переводов. Используемые ниже форматы собраны на основе:
 *  - публичных Universal Links, на которые редиректят сайты банков;
 *  - сторонних агрегаторов (MobiusPay и др.);
 *  - наблюдений за поведением приложений на iOS/Android в 2024–2025 гг.
 * Поэтому форматы могут меняться — обновляйте по мере необходимости.
 */
import {Bank} from '../types/Bank'

export type RequisiteType = 'card' | 'sbp'
export type Platform = 'ios' | 'android'

export interface DeepLinkParams {
    requisiteType: RequisiteType
    /** Номер карты (без пробелов) или номер телефона для СБП. */
    number: string
    /** Сумма в основных единицах (рубли/валюта), не копейки. */
    amount: number
    /** ISO-код страны получателя, напр. 'RU', 'KZ', 'TR'. */
    country: string
    /** Название банка получателя. */
    bank: Bank
}

const sanitizeCard = (value: string) => value.replace(/\D/g, '')
const sanitizePhone = (value: string) => value.replace(/\D/g, '')

const formatAmount = (amount: number) => amount.toFixed(2)

/**
 * Альфа-Банк. Universal Link, который перехватывается приложением.
 *   https://alfabank.ru/get-money/transfers/?account=<номер карты>&amount=<сумма>
 *
 * Для переводов за рубеж — открываем общий раздел «Переводы за рубеж»,
 * без предзаполнения, т.к. приложение требует пошаговый выбор страны/банка.
 */
const buildAlfaLink = (platform: Platform, p: DeepLinkParams): string | null => {
    if (p.country !== 'RU') {
        const params = new URLSearchParams()
        params.set('amount', formatAmount(p.amount))

        if (p.requisiteType === 'card') {
            params.set('account', sanitizeCard(p.number))

            return `https://alfabank.ru/get-money/transfers/?${params.toString()}`
        }

        if (p.requisiteType === 'sbp') {
            params.set('phone', sanitizePhone(p.number))
            params.set('bank', p.bank)

            return `https://alfabank.ru/get-money/sbp/?${params.toString()}`
        }
    }

    // Открываем общий раздел переводов за рубеж — без предзаполнения,
    // т.к. формат для иностранных карт публично не поддерживается.
    return 'https://alfabank.ru/everyday/transfers/abroad/'
}

/**
 * Сбербанк. Открытых deeplink'ов на P2P-перевод по карте у Сбера фактически нет:
 * для своих сервисов используется bank100000000008:// (нативная схема),
 * для интернет-эквайринга — sberpay-страницы.
 *
 * Наиболее надёжный сценарий, работающий в полях для пользователей —
 * Universal Link на форму перевода в Сбербанк Онлайн:
 *   https://online.sberbank.ru/CSAFront/index.do#/payments/transfer?...
 * либо платёжная ссылка СБП:
 *   https://qr.nspk.ru/...
 *
 * Сбер открывает приложение для transfer-ссылок только для своих карт.
 * Для иностранных карт прямого deeplink нет — возвращаем null.
 */
const buildSberLink = (platform: Platform, p: DeepLinkParams): string | null => {
    if (p.country !== 'RU') {
        if (p.requisiteType === 'card') {
            const params = new URLSearchParams()
            params.set('cardNumber', sanitizeCard(p.number))
            params.set('amount', formatAmount(p.amount))

            return `https://online.sberbank.ru/CSAFront/index.do#/payments/transfer?${params.toString()}`
        }

        if (p.requisiteType === 'sbp') {
            const params = new URLSearchParams()
            params.set('phone', sanitizePhone(p.number))
            params.set('amount', formatAmount(p.amount))
            params.set('bank', p.bank)

            return `https://online.sberbank.ru/CSAFront/index.do#/payments/sbp?${params.toString()}`
        }

        return null
    }

    if (platform === 'ios') {
        // Другие возможные варианты
        // bank00000000111://sbolonline/transfers/overseas
        // onlineios-app://sbolonline/transfers/overseas
        // ios-app-smartonline://sbolonline/transfers/overseas
        // budgetonline-ios://sbolonline/transfers/overseas
        // app-online-ios://sbolonline/transfers/overseas
        // btripsexpenses://sbolonline/transfers/overseas
        // onlineappmobile://sbolonline/transfers/overseas

        return 'sberbankonline://sbolonline/transfers/overseas'
    }

    if (platform === 'android') {
        return 'intent://ru.sberbankmobile/transfers/overseas'
    }

    return null
}

/**
 * Т-Банк (Tinkoff). Универсальная ссылка, которая открывает приложение
 * на форме перевода с предзаполненной картой/телефоном и суммой.
 *
 * Карта:    https://www.tbank.ru/transfer/card/?numberTo=...&sum=...&currency=RUB
 * СБП:      https://www.tbank.ru/transfer/sbp/?numberTo=7...&sum=...
 *
 * Поддерживает рублёвые переводы. Для зарубежных карт прямой формат
 * не поддерживается — возвращаем null.
 */
const buildTinkoffLink = (platform: Platform, p: DeepLinkParams): string | null => {
    if (p.country === 'RU') {
        const params = new URLSearchParams()
        params.set('sum', formatAmount(p.amount))
        params.set('currency', 'RUB')

        if (p.requisiteType === 'card') {
            params.set('numberTo', sanitizeCard(p.number))

            return `https://www.tbank.ru/transfer/card/?${params.toString()}`
        }

        if (p.requisiteType === 'sbp') {
            params.set('numberTo', sanitizePhone(p.number))
            params.set('bankTo', p.bank)

            return `https://www.tbank.ru/transfer/sbp/?${params.toString()}`
        }

        return null
    }

    const backIds: Partial<Record<Bank, string>> = {
        [Bank.octobank]: '10016',
        [Bank.asiaalliancebank]: '20014',
        [Bank.garantbank]: '20029',
        [Bank.poytaxtbank]: '20016',
    }

    const bankMemberId = backIds[p.bank] ?? null

    if (bankMemberId === null) {
        return null
    }

    const params = new URLSearchParams()
    params.set('amount', formatAmount(p.amount))

    if (platform === 'android') {
        // Главная схема — старый brand tinkoffbank, международный перевод
        // const scheme = 'tinkoffbank'

        // Новый brand-package (bank100000000004) — для свежих версий T-Bank
        const scheme = 'bank100000000004'

        if (p.requisiteType === 'card') {
            // bankMemberId не нужен — T-Bank определяет банк по BIN карты
            params.set('targetCardNumber', sanitizeCard(p.number))
            params.set('targetCard', sanitizeCard(p.number))

            const query = params.toString()

            return `${scheme}://Main/Pay/C2C?${query}`
        } else {
            params.set('bankMemberId', bankMemberId)
            params.set('workflowType', 'InternationalTransfer')
            params.set('numberPhone', '+' + sanitizePhone(p.number))

            const query = params.toString()

            return `${scheme}://Main/PayByMobileNumber?${query}`
        }
    }

    if (platform === 'ios') {
        // Старый brand tinkoffbank — для не обновлённых приложений
        // let scheme = 'tinkoffbank'

        // Главная iOS схема — новый brand bank100000000004
        let scheme = 'bank100000000004'

        if (p.requisiteType === 'card') {
            params.set('targetCardNumber', sanitizeCard(p.number))
            params.set('targetCard', sanitizeCard(p.number))

            const query = params.toString()

            return `${scheme}://Main/Pay/C2C?${query}`
        } else {
            // Новейший brand tbank:// (последние iOS-версии). Параметр number= вместо numberPhone=
            // tbank://Main/PayByMobileNumber?number=%2B{phone}&amount={amount}&bankMemberId={bankMemberId}
            // Пока не реализуем его, просто для информации

            params.set('bankMemberId', bankMemberId)
            params.set('numberPhone', '+' + sanitizePhone(p.number))

            const query = params.toString()

            return `${scheme}://Main/PayByMobileNumber?${query}`
        }
    }

    return null
}

const buildVtbLink = (platform: Platform, p: DeepLinkParams): string | null => {
    if (p.country === 'RU') {
        const params = new URLSearchParams()
        params.set('deeplink', 'true')
        // Аналогично зарубежной схеме — попробуем оба варианта названия параметра суммы,
        // т.к. публичной документации нет, а разные версии приложения принимают разное.
        params.set('sum', formatAmount(p.amount))
        params.set('amount', formatAmount(p.amount))

        if (p.requisiteType === 'card') {
            // Universal Link на форму «Перевод по номеру карты» внутри ВТБ Онлайн.
            // Формат подсмотрен по аналогии с международной схемой /i/phone/<country>/<bankId>/,
            // для российских C2C-переводов работает шаблон /i/card/RU/.
            params.set('cardNumber', sanitizeCard(p.number))

            return `https://online.vtb.ru/i/card/RU/?${params.toString()}`
        }

        if (p.requisiteType === 'sbp') {
            // Идентификаторы банков-участников СБП на стороне ВТБ Онлайн.
            // Это не bicMemberId НСПК, а внутренние числовые id из их каталога /i/phone/RU/<id>/.
            // Здесь перечислены самые востребованные; список нужно расширять по мере необходимости.
            const ruSbpBankIds: Partial<Record<Bank, string>> = {
                [Bank.sberbank]: '1',
                [Bank.tinkoff]: '2',
                [Bank.alfabank]: '3',
                [Bank.vtb]: '5',
                [Bank.raiffeisen]: '7',
                [Bank.gazprombank]: '8',
                [Bank.pochtabank]: '14',
                // [Bank.rshb]: '9',
                // [Bank.otkritie]: '10',
                // [Bank.sovcombank]: '12',
                // [Bank.mts]: '15',
                // [Bank.ozon]: '20',
            }

            const bankId = ruSbpBankIds[p.bank]
            params.set('phoneNumber', sanitizePhone(p.number))

            if (bankId) {
                return `https://online.vtb.ru/i/phone/RU/${bankId}/?${params.toString()}`
            }

            // Если id банка-получателя в СБП нам неизвестен — отдаём ссылку
            // без него: приложение откроет форму СБП-перевода с предзаполненным
            // телефоном и попросит пользователя выбрать банк вручную.
            return `https://online.vtb.ru/i/phone/RU/?${params.toString()}`
        }

        return null
    }

    // Тут только по номеру
    if (p.requisiteType !== 'sbp') {
        return null
    }

    const params = new URLSearchParams()
    params.set('deeplink', 'true')
    params.set('phoneNumber', sanitizePhone(p.number))

    // Нет информации о том, как передавать сумму
    // Но можно попробовать наиболее очевидные варианты
    params.set('sum', formatAmount(p.amount))
    params.set('amount', formatAmount(p.amount))

    const query = params.toString()

    if (p.bank === Bank.octobank) {
        return `https://online.vtb.ru/i/phone/UZ/49/?${query}`
    }

    if (p.bank === Bank.asiaalliancebank) {
        return `https://online.vtb.ru/i/phone/UZ/50/?${query}`
    }

    if (p.bank === Bank.garantbank) {
        return `https://online.vtb.ru/i/phone/UZ/57/?${query}`
    }

    if (p.bank === Bank.poytaxtbank) {
        return `https://online.vtb.ru/i/phone/UZ/66/?${query}`
    }

    return null
}

/**
 * Возвращает deeplink или null, если для данной комбинации
 * (банк плательщика / тип реквизита / страна получателя) формат не поддерживается.
 */
export const buildBankDeepLink = (payerBank: Bank, platform: Platform, params: DeepLinkParams): string | null => {
    try {
        if (payerBank === Bank.alfabank) {
            return buildAlfaLink(platform, params)
        }

        if (payerBank === Bank.sberbank) {
            return buildSberLink(platform, params)
        }

        if (payerBank === Bank.tinkoff) {
            return buildTinkoffLink(platform, params)
        }

        if (payerBank === Bank.vtb) {
            return buildVtbLink(platform, params)
        }

        return null
    } catch {
        return null
    }
}
